import { Injectable, inject } from '@angular/core';
import { Common } from '../../../shared/enums/common';
import { RideStatusType } from '../../../shared/enums/rideStatusType';
import { RideDocumentDto } from '../../../shared/interfaces/ride-firestore';
import { RideListItem } from '../../../shared/interfaces/ride-list-item';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { ActionDialogService } from './action-dialog.service';
import { AuthService } from '../../../core/auth/services/auth.service';
import { RideMassCancelComponent } from '../components/ride-mass-cancel/ride-mass-cancel.component';
import { SendGuardiansMassNotificationComponent } from '../components/send-guardians-mass-notification/send-guardians-mass-notification.component';
import { CancelStudentRideDialogComponent } from '../components/cancel-student-ride-dialog/cancel-student-ride-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class RideActionsService {
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly authService = inject(AuthService);

  appSetting = this.authService.appSetting();
  disableSendRideOffer(ride: RideListItem): boolean {
    return ride.driverDetails?.id == Common.PartnerBotId || ride.rideStatus?.toLowerCase() == 'driver not assigned' || ride.JobStatusId != RideStatusType.None;
  }

  disableResetRide(ride: RideListItem): boolean {
    return ride.rideStatusId == RideStatusType.CanceledDueToPandemic || ride.rideStatusId == RideStatusType.CanceledInAdvance || ride.rideStatusId == RideStatusType.CanceledLate || ride.rideStatusId == RideStatusType.CanceledDueToHoliday;
  }

  disableUnassignDriver(ride: RideListItem): boolean {
    return !(ride.rideStatusId == RideStatusType.None && (ride.JobStatusId == RideStatusType.None || ride.JobStatusId == RideStatusType.Offered) && ride.driverDetails?.id != Common.PartnerBotId);
  }

  disableIgnoreDoubleBooked(ride: RideListItem): boolean {
    return !(ride.rideStatus?.toLowerCase() == 'driver 2x booked' || ride.rideStatus?.toLowerCase() == 'driver & monitor 2x booked');
  }

  disableIgnoreMonitorDoubleBooked(ride: RideListItem): boolean {
    return !(ride.rideStatus?.toLowerCase() == 'monitor 2x booked' || ride.rideStatus?.toLowerCase() == 'driver & monitor 2x booked');
  }

  viewSendRideOffer(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasSendRideOfferPermission ?? false) && !isPendingRides;
  }
  viewResetRide(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasResetRidePermission ?? false) && !isPendingRides;
  }
  viewUnassignDriver(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasUnassignDriverPermission ?? false) && !isPendingRides;
  }
  viewIgnoreDoubleBooked(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasIgnoreDoubleBookedPermission ?? false) && !isPendingRides;
  }
  viewIgnoreMonitorDoubleBooked(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasIgnoreMonitorDoubleBookedPermission ?? false) && !isPendingRides;
  }

  viewRideActivity(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasViewRideActivityPermission ?? false) && !isPendingRides;
  }
  viewCompleteRide(): boolean {
    return this.appSetting?.hasCompleteRidePermission ?? false;
  }
  viewDeleteRide(): boolean {
    return this.appSetting?.hasDeleteRidePermission ?? false;
  }
  viewAddIncidentReport(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasAddIncidentReportPermission ?? false) && !isPendingRides;
  }
  viewSendDelayNotification(isPendingRides: boolean = false): boolean {
    return (this.appSetting?.hasSendDelayNotificationPermission ?? false) && !isPendingRides;
  }
  viewCancelRide(ride: RideListItem): boolean {
    return (
      this.appSetting?.hasMassCancellation &&
      ride.rideStatusId != RideStatusType.CanceledDueToPandemic &&
      ride.rideStatusId != RideStatusType.CanceledInAdvance &&
      ride.rideStatusId != RideStatusType.CanceledLate &&
      ride.rideStatusId != RideStatusType.CanceledDueToHoliday &&
      ride.rideStatusId != RideStatusType.Completed
    );
  }

  viewAutoAcceptRideButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return (
      (ride.rideStatusId == RideStatusType.Offered || ride.rideStatusId == RideStatusType.None) &&
      ride.driverDetails?.id != Common.PartnerBotId &&
      ride.JobStatusId != RideStatusType.CanceledDueToHoliday &&
      ride.JobStatusId != RideStatusType.CanceledInAdvance &&
      ride.JobStatusId != RideStatusType.CanceledLate &&
      ride.JobStatusId != RideStatusType.Accepted &&
      ride.JobStatusId != RideStatusType.Declined &&
      !isPendingRides &&
      (this.appSetting?.hasAutoAcceptRidePermission ?? false)
    );
  }

  openMassCancelRidesDialog(dialogService: DialogService, messageService: MessageService, ride?: RideListItem, docRide?: RideDocumentDto, isSingleRideCancellation: boolean = false, isPendingRides: boolean = false) {
    const ref = dialogService.open(RideMassCancelComponent, {
      showHeader: false,
      width: '640px',
      modal: true,
      data: {
        ride,
        isSingleRideCancellation,
        isPendingRides,
      },
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '768px': '90vw',
      },
    })!;

    ref.onClose.subscribe((result: { confirmed: boolean } | undefined) => {
      if (result?.confirmed) {
        const messageDetail = isSingleRideCancellation ? 'Ride cancelled successfully.' : 'Rides cancelled successfully.';
        messageService.add({ severity: 'success', summary: 'Success', detail: messageDetail });
      }
    });
  }

  openGuardianMassNotificationDialog(dialogService: DialogService, messageService: MessageService, clients?: any, places?: any, states?: any) {
    const ref = dialogService.open(SendGuardiansMassNotificationComponent, {
      showHeader: false,
      width: '800px',
      modal: true,
      data: {
        clients,
        places,
        states,
      },
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '992px': '90vw',
      },
    })!;

    ref.onClose.subscribe((result: { confirmed: boolean } | undefined) => {
      if (result?.confirmed) {
        const messageDetail = `Notification sent successfully.`;
        messageService.add({ severity: 'success', summary: 'Success', detail: messageDetail });
      }
    });
  }

  openCancelStudentRideDialog(dialogService: DialogService, messageService: MessageService, students: any, periods: any, currentDate: string) {
    const ref = dialogService.open(CancelStudentRideDialogComponent, {
      showHeader: false,
      width: '1000px',
      modal: true,
      data: {
        students,
        periods,
        currentDate,
      },
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '1200px': '800px',
        '992px': '640px',
        '768px': '90vw',
      },
    })!;

    ref.onClose.subscribe((result: { confirmed: boolean } | undefined) => {
      if (result?.confirmed) {
        const messageDetail = `Student(s) Ride canceled successfully!.`;
        messageService.add({ severity: 'success', summary: 'Success', detail: messageDetail });
      }
    });
  }

  viewAutoStartRideButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return ride.JobStatusId == RideStatusType.Accepted && (this.appSetting?.hasAutoStartPermission ?? false) && !isPendingRides;
  }

  viewAssignSubDriverButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return ride.rideStatusId == RideStatusType.None && (this.appSetting?.hasAssignSubDriverPermission ?? false) && !isPendingRides;
  }

  viewAddPayoutAdjustmentButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return ride.driverDetails?.id != Common.PartnerBotId && (this.appSetting?.hasPayoutAdjustmentPermission ?? false) && !isPendingRides;
  }

  viewFindDriverButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return ride.rideStatusId == RideStatusType.None && (this.appSetting?.hasFindDriverPermission ?? false) && !isPendingRides;
  }

  viewChangeRideTimeButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return ride.rideStatusId == RideStatusType.None && (this.appSetting?.hasChangeRideTimePermission ?? false) && !isPendingRides;
  }

  viewWaitingTimeButton(ride: RideListItem, isPendingRides: boolean = false): boolean {
    return (
      ride.rideStatusId != RideStatusType.None &&
      ride.rideStatusId != RideStatusType.CanceledDueToHoliday &&
      ride.rideStatusId != RideStatusType.CanceledDueToPandemic &&
      ride.rideStatusId != RideStatusType.CanceledInAdvance &&
      ride.rideStatusId != RideStatusType.CanceledLate &&
      !isPendingRides &&
      (this.appSetting?.hasViewWaitingTimePermission ?? false)
    );
  }

  // ====================

  openSendRideOfferDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'sendRideOffer',
      header: 'Send Ride Offer Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: 'Are you sure you want to send this ride offer?',
      onConfirmedMessage: 'The ride offer was sent successfully',
      hasInput: true,
      inputPlaceholder: 'Please enter a reason in order to send ride offer.',
    });
  }
  openAutoAcceptRideDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'autoAcceptRide',
      header: 'Auto Accept Ride Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: 'Are you sure you want to auto accept this ride?',
      onConfirmedMessage: 'Auto accept rides was successfully updated',
      hasInput: true,
      inputPlaceholder: 'Please enter a reason in order to auto accept this ride.',
    });
  }
  openAutoStartRideDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'autoStartRide',
      header: 'Auto Start Ride Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: 'Are you sure you want to auto start this ride?',
      onConfirmedMessage: 'Auto start rides was successfully updated',
      hasInput: true,
      inputPlaceholder: 'Please enter a reason in order to auto start this ride.',
    });
  }
  openResetRideDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDeleteDialog({
      dialogService: dialogService,
      action: 'resetRide',
      header: 'Reset Ride',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      onConfirmedMessage: 'Reset ride request in progress.',
      inputPlaceholder: 'The reason for why you are resetting this ride',
      warningMessages: [`Resetting a ride will take it back to the original status it was created in.`, `This action will notify the driver and any monitors via push Notification.`],
    });
  }
  WaypointsActivityLogDialog(dialogService: DialogService, originalRideId: number, stopId: any, rideTimeline: any, timezoneOffsetInSeconds: number) {
    this.actionDialogService.openWaypointsActivityLogDialog(dialogService, originalRideId, stopId, rideTimeline, timezoneOffsetInSeconds);
  }

  openRideActivityDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`rideActivity`, dialogService, ride, docRide, messageService);
  }
  openChangeRideTimeDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`changeRideTime`, dialogService, ride, docRide, messageService, `The ETA arrival time was changes successfully`);
  }
  openCompleteRideDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`completeRide`, dialogService, ride, docRide, messageService, `Ride ${ride.id} was completed successfully.`);
  }
  openWaitingTimeDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`waitingTime`, dialogService, ride, docRide, messageService, `Waiting time was updated successfully.`);
  }
  openDeleteRideDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDeleteDialog({
      dialogService: dialogService,
      action: 'deleteRide',
      header: 'Delete Ride',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      onConfirmedMessage: 'Ride deleted Successfully.',
      inputPlaceholder: 'The reason for why you are deleting this ride',
      warningMessages: [`This action will notify the driver and any monitors via push Notification.`],
    });
  }
  openIgnoreDriverDoubleBookedDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'ignoreDriverDoubleBooked',
      header: 'Ignore Double Booked Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: `Are you sure you want to Ignore Double Booked for ${ride.driverDetails?.name} ?`,
      onConfirmedMessage: 'The ignore double booked was updated successfully.',
      btnLabel: 'Ignore',
    });
  }

  openIgnoreMonitorDoubleBookedDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'ignoreMonitorDoubleBooked',
      header: 'Ignore Double Booked Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: `Are you sure you want to Ignore Double Booked for ${ride.monitorDetails?.name} ?`,
      onConfirmedMessage: 'The ignore double booked was updated successfully.',
      btnLabel: 'Ignore',
    });
  }
  openUnassignDriverDialog(ride: RideListItem, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openConfirmDialog({
      dialogService: dialogService,
      action: 'unAssignDriver',
      header: 'Un-assign Driver Confirmation',
      messageService: messageService,
      id: ride.id.toString(),
      uuid: ride.uuid,
      confirmationText: 'Are you sure you want to un-assign driver? This action is permanent.',
      onConfirmedMessage: 'The driver was un-assigned successfully.',
    });
  }
  openAssignSubDriverDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService, selectedDriverId?: number) {
    this.actionDialogService.openDialog(`assignSubDriver`, dialogService, ride, docRide, messageService, `Driver Assigned Request In Progress.`, selectedDriverId);
  }
  openProviderAssignSubDriverDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService, selectedDriverId?: number) {
    this.actionDialogService.openDialog(`providerAssignSubDriver`, dialogService, ride, docRide, messageService, `Driver Assigned Request In Progress.`, selectedDriverId);
  }
  openFindDriverDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`findDriver`, dialogService, ride, docRide, messageService);
  }
  openIncidentReportDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`addIncidentReport`, dialogService, ride, docRide, messageService, `The incident report has been added successfully.`);
  }
  openPayoutAdjustmentDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`payoutAdjustment`, dialogService, ride, docRide, messageService, `The payout adjustment was added successfully.`);
  }
  openDelayNotificationDialog(ride: RideListItem, docRide: RideDocumentDto, dialogService: DialogService, messageService: MessageService) {
    this.actionDialogService.openDialog(`delayNotification`, dialogService, ride, docRide, messageService, `The notification was sent successfully.`);
  }
}
